import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { FixedContentPreview } from './FixedContentPreview';
import { AboutPageRichTextEditor } from './AboutPageRichTextEditor';
import {
  useGlobalizationContext,
  useTranslation,
  useNotificationContext,
  useGetUiAdminConfigEntry,
  useUpsertUiAdminConfigEntry
} from '@cs-cms/state-management';
import { ABOUT_PAGE_SETTINGS_KEY, DEFAULT_ABOUT_CONTENT, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';

export const AboutPageEditor: React.FC = () => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showToast } = useNotificationContext();
  const { data: fetchedContent, isLoading } = useGetUiAdminConfigEntry(ABOUT_PAGE_SETTINGS_KEY);

  const { mutate: saveContent, isPending: isPending } = useUpsertUiAdminConfigEntry();

  const [content, setContent] = React.useState(DEFAULT_ABOUT_CONTENT);

  React.useEffect(() => {
    if (fetchedContent) {
      try {
        const parsed = JSON.parse(fetchedContent);
        if (parsed.info) {
          setContent(parsed.info);
        } else {
          setContent(fetchedContent);
        }
      } catch {
        setContent(fetchedContent);
      }
    }
  }, [fetchedContent]);

  const titleText = translate('custom.settings.itAdminCenter.aboutPageEditor.title');
  const descriptionText = translate('custom.settings.itAdminCenter.aboutPageEditor.description');
  const pageContentLabel = translate('custom.settings.itAdminCenter.aboutPageEditor.pageContent');
  const pageContentDescription = translate('custom.settings.itAdminCenter.aboutPageEditor.pageContentDescription');
  const savingChangesText = translate('custom.common.saving');
  const saveChangesText = translate('custom.common.saveChangesTitle');

  const handleSave = () => {
    saveContent(
      {
        key: ABOUT_PAGE_SETTINGS_KEY,
        body: content
      },
      {
        onSuccess: () => {
          showToast({
            type: CPS_NOTIFICATION_TYPE.SUCCESS,
            message: translate('custom.settings.itAdminCenter.aboutPageEditor.changesSaved')
          });
        }
      }
    );
  };

  return (
    <div>
      <h1 className="aboutPageEditorTitle">{titleText}</h1>
      <p>{descriptionText}</p>

      <div className="aboutPageEditorCard">
        <div className="aboutPageEditorContent">
          <div>
            <label className="aboutPageEditorLabel">{pageContentLabel}</label>
            <p className="aboutPageEditorHelperText">{pageContentDescription}</p>
            <AboutPageRichTextEditor value={content} onChange={setContent} />
          </div>

          <FixedContentPreview />

          <div className="aboutPageEditorActions">
            <Button
              type="button"
              onClick={handleSave}
              themeColor="primary"
              className="aboutPageEditorButton"
              disabled={isPending || isLoading}
            >
              {isPending ? savingChangesText : saveChangesText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
